import type { GlossaryEntry } from '@atlas/domain';
import { CORE_CONTENT } from './core-content.js';
import { GLOSSARY_TERMS, type GlossaryTermRef } from './registry.js';

const CATEGORY_CONTEXT: Readonly<Record<GlossaryTermRef['category'], string>> = {
  macro: 'Macro concepts define the policy, inflation, growth, labor, and liquidity envelope in which assets are priced.',
  risk: 'Risk signals are cross-checks for regime classification; no single print is a self-sufficient crisis detector.',
  commodities: 'Commodity analysis separates physical scarcity, demand, inventories, and futures mechanics before interpreting price.',
  fx: 'FX analysis maps reserve roles, policy management, funding carry, and external stress across currencies and venues.',
  market_structure: 'Market structure identifies who owns risk, how flows transmit, and where price can diverge from fundamentals.',
  geopolitics: 'Geopolitical analysis maps principals, constraints, bargaining power, and equilibrium before projecting market effects.',
  tps: 'TPS analysis traces protected private nodes, their hosts, rent capture, and the conditions under which immunity disappears.',
  valuation: 'Valuation translates cash-flow durability and explicit assumptions into auditable ranges rather than point certainty.',
  portfolio: 'Portfolio analysis puts survival, liquidity, concentration, and household constraints ahead of return maximization.',
  learning: 'Learning separates luck from repeatable quality with point-in-time forecasts, holdouts, and controlled policy changes.',
  provenance: 'Provenance records what was true, what was known, when it arrived, and whether evidence supports a conclusion.',
};

const CATEGORY_SOURCE: Readonly<Record<GlossaryTermRef['category'], string>> = {
  macro: 'src-fred',
  risk: 'src-fixture-market',
  commodities: 'src-eia',
  fx: 'src-fixture-market',
  market_structure: 'src-sec-edgar',
  geopolitics: 'src-csc-corpus',
  tps: 'src-csc-corpus',
  valuation: 'src-model',
  portfolio: 'src-model',
  learning: 'src-model',
  provenance: 'src-doctrine-fixture',
};

function relatedFor(ref: GlossaryTermRef): GlossaryEntry['relatedTermIds'] {
  return GLOSSARY_TERMS
    .filter((candidate) => candidate.category === ref.category && candidate.termId !== ref.termId)
    .slice(0, 3)
    .map((candidate) => candidate.termId as GlossaryEntry['termId']);
}

function createEntry(ref: GlossaryTermRef): GlossaryEntry {
  const core = CORE_CONTENT[ref.slug];
  const definition =
    core?.definition ??
    `${ref.term} is a defined ${ref.category.replace('_', ' ')} concept that turns observable evidence into a reviewable analytical state.`;
  const mechanism =
    core?.mechanism ??
    `${ref.term} must be read through its stated scope, method, horizon, and source provenance. Atlas does not promote it from an observation to a portfolio conclusion until related signals and explicit falsifiers are checked.`;
  const derivation =
    core?.derivation ??
    `Derive or classify ${ref.term} only with a named source, valid-at and known-at timestamps, declared units or rubric, and an unavailable state for missing inputs.`;
  return {
    termId: ref.termId as GlossaryEntry['termId'],
    slug: ref.slug,
    term: ref.term,
    aliases: ref.aliases,
    category: ref.category,
    oneSentence: definition,
    fullExplanationMdx: `${definition}\n\n${mechanism} ${CATEGORY_CONTEXT[ref.category]} A valid reading states what the concept does **not** establish and preserves partial, contradicted, or unavailable evidence rather than filling gaps with a convenient proxy.\n\n${derivation}`,
    whyItMatters: `${ref.term} can alter expected cash flow, discount rates, scenario weights, or portfolio constraints; a bad reading creates false conviction and hidden concentration.`,
    howItIsCalculatedOrDerived: derivation,
    howToReadIt: `Read ${ref.term} across level or state, direction, persistence, and confirmation from related concepts. Check source vintage and route context before comparing periods or acting.`,
    highLowOrStateInterpretation: 'High, low, rising, falling, normal, stressed, partial, and unavailable states are meaningful only under the declared method and window; unavailable is not neutral.',
    cscStructuralLens: `${CATEGORY_CONTEXT[ref.category]} The Cold State lens asks which principal or protected node controls the underlying constraint and how control reaches cash flow before price.`,
    workedExample: `A point-in-time review records the sourced ${ref.term} observation, checks related terms and falsifiers, and withholds action when evidence is partial. Fixture values are never live facts.`,
    commonMisreadings: [
      `Treating ${ref.term} as a standalone buy or sell signal`,
      `Comparing ${ref.term} across mismatched methods, vintages, units, or horizons`,
    ],
    limitationsAndFalsifiers: [
      `The interpretation fails when the stated mechanism for ${ref.term} no longer explains the observations or a stronger competing mechanism does`,
      'Stale, revised, partial, or contradicted inputs require a downgraded or unavailable conclusion',
    ],
    relatedTermIds: relatedFor(ref),
    usedOnRoutes: ref.usedOnRoutes,
    sourceIds: [CATEGORY_SOURCE[ref.category]],
    reviewedAt: '2026-07-27T00:00:00.000Z',
    version: core ? '1.0.0' : '0.1.0',
  };
}

export const CORE_TERM_COUNT = Object.keys(CORE_CONTENT).length;
export const GLOSSARY_REGISTRY: readonly GlossaryEntry[] = GLOSSARY_TERMS.map(createEntry);

export interface MetricRegistration {
  metricId: string;
  label: string;
  glossaryTermId: string;
  routes: string[];
}

export const METRIC_REGISTRY: readonly MetricRegistration[] = GLOSSARY_TERMS.map((term) => ({
  metricId: `taxonomy.${term.category}.${term.slug}`,
  label: term.term,
  glossaryTermId: term.termId,
  routes: term.usedOnRoutes,
}));

export function getGlossaryBySlug(slug: string): GlossaryEntry | undefined {
  return GLOSSARY_REGISTRY.find((g) => g.slug === slug);
}

export function searchGlossary(query: string): GlossaryEntry[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return GLOSSARY_REGISTRY.filter(
    (g) =>
      g.term.toLowerCase().includes(q) ||
      g.oneSentence.toLowerCase().includes(q) ||
      g.fullExplanationMdx.toLowerCase().includes(q) ||
      g.aliases.some((a) => a.toLowerCase().includes(q)),
  );
}

export function getRelatedEntries(termId: string): GlossaryEntry[] {
  const entry = GLOSSARY_REGISTRY.find((candidate) => candidate.termId === termId);
  if (!entry) return [];
  const related = new Set(entry.relatedTermIds);
  return GLOSSARY_REGISTRY.filter(
    (candidate) => related.has(candidate.termId) || candidate.relatedTermIds.includes(entry.termId),
  );
}

export function getEntriesForRoute(route: string): GlossaryEntry[] {
  return GLOSSARY_REGISTRY.filter((entry) => entry.usedOnRoutes.includes(route));
}

export interface GlossaryRelationGraph {
  nodes: Array<Pick<GlossaryEntry, 'termId' | 'slug' | 'term' | 'category'>>;
  edges: Array<{ from: string; to: string }>;
}

export function buildRelationGraph(): GlossaryRelationGraph {
  const edges = new Map<string, { from: string; to: string }>();
  for (const entry of GLOSSARY_REGISTRY) {
    for (const related of entry.relatedTermIds) {
      const [from, to] = [entry.termId, related].sort();
      edges.set(`${from}|${to}`, { from, to });
    }
  }
  return {
    nodes: GLOSSARY_REGISTRY.map(({ termId, slug, term, category }) => ({
      termId,
      slug,
      term,
      category,
    })),
    edges: [...edges.values()],
  };
}

export function findRelationPath(fromTermId: string, toTermId: string): string[] | undefined {
  if (fromTermId === toTermId) return [fromTermId];
  const neighbors = new Map<string, string[]>();
  for (const edge of buildRelationGraph().edges) {
    neighbors.set(edge.from, [...(neighbors.get(edge.from) ?? []), edge.to]);
    neighbors.set(edge.to, [...(neighbors.get(edge.to) ?? []), edge.from]);
  }
  const queue: string[][] = [[fromTermId]];
  const visited = new Set([fromTermId]);
  while (queue.length) {
    const path = queue.shift()!;
    for (const next of neighbors.get(path.at(-1)!) ?? []) {
      if (visited.has(next)) continue;
      const candidate = [...path, next];
      if (next === toTermId) return candidate;
      visited.add(next);
      queue.push(candidate);
    }
  }
  return undefined;
}
